import { ConnectGstin, FileOnPortal, useGstConnection } from "./PortalFiling";
import { GstAdvances } from "../vouchers/AdvancePrompt";
import { Gstr1Amended } from "./Gstr1Extras";
import { GstPayPanel } from "./GstPayment";
import { gstLiveApi } from "../lib/api";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { gstApi, reportsApi, type Row } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad, errText } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { Table } from "../reports/ReportPage";
import type { Col } from "../reports/registry";
import { download, readText } from "../ui/download";
import { fmtDate } from "../companies/CompanyPicker";
import { iso } from "../reports/period";
import { toUrl } from "../shell/routes";

const a = (key: string, label: string): Col => ({ key, label, kind: "amount", total: true });
const T = {
  summary: [{ key: "section", label: "Table" }, { key: "documents", label: "Documents", kind: "int", total: true }, a("taxable_value", "Taxable"), a("igst", "IGST"), a("cgst", "CGST"), a("sgst", "SGST"), a("cess", "Cess")] as Col[],
  hsn: [{ key: "tab", label: "" }, { key: "hsn_sac", label: "HSN / SAC" }, { key: "uqc", label: "UQC" }, { key: "quantity", label: "Qty", kind: "qty" }, { key: "rate", label: "Rate", kind: "pct" },
        a("taxable_value", "Taxable"), a("igst", "IGST"), a("cgst", "CGST"), a("sgst", "SGST"), a("cess", "Cess")] as Col[],
  docs: [{ key: "doc_kind", label: "Documents" }, { key: "series", label: "Series" }, { key: "from_number", label: "From" }, { key: "to_number", label: "To" },
         { key: "total", label: "Total", kind: "int" }, { key: "cancelled", label: "Cancelled", kind: "int" }, { key: "net_issued", label: "Net", kind: "int" }] as Col[],
  checks: [{ key: "code", label: "Check" }, { key: "message", label: "What to fix" }, { key: "voucher_number", label: "Document" }] as Col[],
  r3b: [{ key: "tbl", label: "Table" }, { key: "label", label: "" }, { key: "pos", label: "POS" }, { key: "taxable_value", label: "Taxable", kind: "amount" },
        { key: "igst", label: "IGST", kind: "amount" }, { key: "cgst", label: "CGST", kind: "amount" }, { key: "sgst", label: "SGST", kind: "amount" }, { key: "cess", label: "Cess", kind: "amount" }] as Col[],
  setoff: [{ key: "head", label: "Tax" }, a("liability", "Liability"), a("rcm_liability", "RCM"), a("credit_bf", "Credit b/f"), a("credit_period", "Credit (period)"),
           a("paid_by_igst", "By IGST"), a("paid_by_cgst", "By CGST"), a("paid_by_sgst", "By SGST"), a("paid_by_cess", "By cess"), a("cash", "Cash"), a("credit_cf", "Credit c/f")] as Col[],
  itc: [{ key: "head", label: "Tax" }, a("claimed_in_3b", "Claimed in 3B"), a("available_in_2b", "Available in 2B"), a("excess", "Excess"), { key: "status", label: "Status" }] as Col[],
  r2b: [{ key: "status", label: "Status" }, { key: "supplier_gstin", label: "Supplier" }, { key: "doc_no", label: "Document" }, { key: "doc_date", label: "Date", kind: "date" },
        { key: "voucher_number", label: "In books" }, a("b2b_taxable", "2B taxable"), a("books_taxable", "Books taxable"), { key: "ims_action", label: "IMS" }, { key: "suggested_action", label: "Suggested" }] as Col[],
};

// GST › Returns: one GSTIN and period at a time — GSTR-1, GSTR-3B and GSTR-2B with IMS
export function GstReturns({ tab: fixed }: { tab: "gstr1" | "gstr3b" | "gstr2b" }) {
  const { company, ref } = useCompany();
  const [sp, setSp] = useSearchParams();
  const regs = ref?.gst_registrations ?? [];
  const reg = sp.get("registration") ?? regs[0]?.id ?? "";
  const t = new Date();
  const period = sp.get("period") ?? iso(new Date(t.getFullYear(), t.getMonth() - 1, 1));
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); n.set(k, v); setSp(n, { replace: true }); };
  const [rev, setRev] = useState(0);
  const p = useLoad(async () => (reg ? gstApi.period(company.id, reg, period) : null), [company.id, reg, period, rev]);

  if (!ref) return <section className="page"><Loading /></section>;
  if (!regs.length) return <section className="page"><PageHead crumb="GST" title={TITLE[fixed]} /><p className="muted">Add the company's GSTIN under Company › Settings first.</p></section>;
  return (
    <section className="page">
      <PageHead crumb="GST" title={TITLE[fixed]} />
      <div className="toolbar params">
        {regs.length > 1 && <label>GSTIN<select value={reg} onChange={(e) => set("registration", e.target.value)}>{regs.map((r) => <option key={r.id} value={r.id}>{r.gstin}</option>)}</select></label>}
        <label>Period<input type="month" value={period.slice(0, 7)} onChange={(e) => e.target.value && set("period", `${e.target.value}-01`)} /></label>
        {p.data && <span className="muted small">{p.data.gstin} · {fmtDate(p.data.from)} to {fmtDate(p.data.to)} · {p.data.frequency}</span>}
      </div>
      <ErrorNote text={p.error?.message} />
      {regs.find((x) => x.id === reg)?.reg_type === "isd" && <p className="notice">{regs.find((x) => x.id === reg)!.gstin} is an Input Service Distributor: it files GSTR-6, not GSTR-1 or GSTR-3B. See <a href={toUrl("gst/isd")}>Input Service Distributor (GS09)</a>.</p>}
      {p.data?.composition && <p className="notice">{p.data.gstin} is under the composition scheme: it doesn't file GSTR-1 or GSTR-3B. Its quarterly CMP-08 and annual GSTR-4 are under <a href={toUrl("gst/composition")}>Composition (GS04)</a>.</p>}
      {p.data && !p.data.composition && (fixed === "gstr1" ? <Gstr1 reg={reg} period={p.data.from} info={p.data} done={() => setRev(rev + 1)} />
        : fixed === "gstr3b" ? <Gstr3b reg={reg} period={p.data.from} info={p.data} done={() => setRev(rev + 1)} />
        : <Gstr2b reg={reg} period={p.data.from} done={() => setRev(rev + 1)} showPeriod={(x) => set("period", x)} />)}
    </section>
  );
}
const TITLE = { gstr1: "GSTR-1", gstr3b: "GSTR-3B", gstr2b: "GSTR-2B and IMS" } as const;

function useReport(name: string, params: Record<string, unknown>, rev = 0) {
  const { company } = useCompany();
  return useLoad(() => reportsApi.run(company.id, name, params), [company.id, name, JSON.stringify(params), rev]);
}

function Filed({ reg, period, ret, filedOn, can, done }: { reg: string; period: string; ret: "gstr1" | "gstr3b"; filedOn: string | null; can: boolean; done: () => void }) {
  const { company } = useCompany();
  const [on, setOn] = useState(iso(new Date()));
  const [err, setErr] = useState<string | null>(null);
  if (filedOn) return <p className="msg ok">Filed on {fmtDate(filedOn)}. Its invoices are locked.</p>;
  if (!can) return null;
  return (
    <div className="fields inline">
      <label>Filed on<input type="date" value={on} max={iso(new Date())} onChange={(e) => setOn(e.target.value)} /></label>
      <button onClick={() => gstApi.markFiled(company.id, reg, period, ret, on).then(done).catch((e) => setErr(errText(e)))}>Mark as filed</button>
      <span className="muted small">Marking it filed locks the period's invoices against changes.</span>
      <ErrorNote text={err} />
    </div>
  );
}

function Gstr1({ reg, period, info, done }: { reg: string; period: string; info: { gstin: string; gstr1_filed_on: string | null; can: { file: boolean } }; done: () => void }) {
  const { company } = useCompany();
  const nav = useNavigate();
  const q = { registration: reg, period };
  const sum = useReport("gstr1_summary", q), hsn = useReport("gstr1_hsn", q), docs = useReport("gstr1_docs", q), checks = useReport("gstr1_checks", q);
  const [err, setErr] = useState<string | null>(null);
  const getJson = () => gstApi.gstr1Json(company.id, reg, period)
    .then((j) => download(`GSTR1_${info.gstin}_${period.slice(5, 7)}${period.slice(0, 4)}.json`, JSON.stringify(j))).catch((e) => setErr(errText(e)));
  return (
    <div className="stack">
      <div className="row"><button className="primary" onClick={getJson}>Download GSTR-1 JSON</button>
        <button className="link" onClick={() => nav(toUrl(`gst/r/gstr1_rows?registration=${reg}&from=${period}&to=${period.slice(0, 8)}${new Date(+period.slice(0, 4), +period.slice(5, 7), 0).getDate()}`))}>Every document</button></div>
      <ErrorNote text={err} />
      {checks.data && checks.data.length > 0 && <><h3 className="warn">Fix these before filing ({checks.data.length})</h3><Table def={{ columns: T.checks }} rows={checks.data} /></>}
      <h3>Summary</h3>{sum.data ? <Table def={{ columns: T.summary }} rows={sum.data} /> : <Loading />}
      <GstAdvances company={company.id} registration={reg} period={period} />
      <Gstr1Amended reg={reg} period={period} />
      <h3>HSN summary</h3>{hsn.data ? <Table def={{ columns: T.hsn }} rows={hsn.data} /> : <Loading />}
      <h3>Documents issued</h3>{docs.data ? <Table def={{ columns: T.docs }} rows={docs.data} /> : <Loading />}
      {info.can.file && <FileOnPortal reg={reg} period={period} ret="gstr1" filedOn={info.gstr1_filed_on} done={done} />}
      <Filed reg={reg} period={period} ret="gstr1" filedOn={info.gstr1_filed_on} can={info.can.file} done={done} />
    </div>
  );
}

function Gstr3b({ reg, period, info, done }: { reg: string; period: string; info: { gstin: string; gstr3b_filed_on: string | null; itc_blocked: boolean; can: { file: boolean } }; done: () => void }) {
  const { company } = useCompany();
  const q = { registration: reg, period };
  const r = useReport("gstr3b", q), so = useReport("gstr3b_setoff", q), itc = useReport("gstr3b_itc_check", q);
  const [err, setErr] = useState<string | null>(null);
  const getJson = () => gstApi.gstr3bJson(company.id, reg, period)
    .then((j) => download(`GSTR3B_${info.gstin}_${period.slice(5, 7)}${period.slice(0, 4)}.json`, JSON.stringify(j))).catch((e) => setErr(errText(e)));
  return (
    <div className="stack">
      <div className="row"><button className="primary" onClick={getJson}>Download GSTR-3B JSON</button></div>
      <ErrorNote text={err} />
      {info.itc_blocked && <p className="notice">The ITC claimed is more than GSTR-2B shows; the portal will block this return. See the ITC check below.</p>}
      <h3>Return</h3>{r.data ? <Table def={{ columns: T.r3b }} rows={r.data} /> : <Loading />}
      <h3>Payment and set-off</h3>{so.data ? <Table def={{ columns: T.setoff }} rows={so.data} /> : <Loading />}
      <h3>ITC against GSTR-2B</h3>{itc.data ? <Table def={{ columns: T.itc }} rows={itc.data} /> : <Loading />}
      <GstPayPanel reg={reg} period={period} filedOn={info.gstr3b_filed_on} done={done} />
      {info.can.file && <FileOnPortal reg={reg} period={period} ret="gstr3b" filedOn={info.gstr3b_filed_on} done={done} />}
      <Filed reg={reg} period={period} ret="gstr3b" filedOn={info.gstr3b_filed_on} can={info.can.file} done={done} />
    </div>
  );
}

function Gstr2b({ reg, period, done, showPeriod }: { reg: string; period: string; done: () => void; showPeriod: (p: string) => void }) {
  const { company } = useCompany();
  const [rev, setRev] = useState(0);
  const rec = useReport("gstr2b_reconcile", { registration: reg, period }, rev);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [sel, setSel] = useState<Row | null>(null);
  const upload = async (f: File | undefined) => {
    if (!f) return;
    setErr(null); setMsg(null);
    try {
      const j = JSON.parse(await readText(f));
      const r = await gstApi.import2b(company.id, reg, j);
      // show the month the file was for, which may not be the one on screen
      setMsg(`Imported ${r.documents} documents for ${new Date(r.period + "T00:00:00").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}.`); setRev(rev + 1); done();
      if (r.period.slice(0, 7) !== period.slice(0, 7)) showPeriod(r.period);
    } catch (x) { setErr(x instanceof SyntaxError ? "That file isn't GSTR-2B JSON." : errText(x)); }
  };
  const conn = useGstConnection(reg);
  const [remark, setRemark] = useState("");
  const act = (action: string) => {
    if (!sel) return;
    if (action === "reject" && !remark.trim()) { setErr("Say why you're rejecting it (the remark goes to the supplier through IMS)."); return; }
    setErr(null);
    gstApi.ims(company.id, reg, String(sel.gstr2b_doc_id), action, remark.trim() || undefined).then(() => { setSel(null); setRemark(""); setRev(rev + 1); }).catch((e) => setErr(errText(e)));
  };
  return (
    <div className="stack">
      <div className="row">
        <label className="file">Import GSTR-2B (JSON from the portal)<input type="file" accept=".json,application/json" onChange={(e) => void upload(e.target.files?.[0])} /></label>
        {conn.data?.connected && <button className="primary" onClick={() => { setErr(null); gstLiveApi.pull2b(company.id, reg, period).then((r) => { setMsg(`Pulled ${r.documents} documents from the portal.`); setRev(rev + 1); done(); }).catch((x) => setErr(errText(x))); }}>Pull from the portal</button>}
        {conn.data?.connected && <button onClick={() => { setErr(null); gstLiveApi.imsPush(company.id, reg).then((r) => setMsg(r.sent ? `Sent ${r.sent} IMS decision${r.sent === 1 ? "" : "s"} to the portal.` : "No new IMS decisions to send.")).catch((x) => setErr(errText(x))); }}>Send IMS decisions</button>}
      </div>
      {conn.data && !conn.data.connected && conn.data.gsp && <ConnectGstin reg={reg} c={conn.data} onChange={(x) => conn.setData(x)} />}
      <ErrorNote text={err} />{msg && <p className="msg ok">{msg}</p>}
      <h3>Supplier documents against your books</h3>
      <p className="muted small">Click a document from GSTR-2B to accept, reject or keep it pending in IMS.</p>
      {rec.data ? <Table def={{ columns: T.r2b }} rows={rec.data} onOpen={(r) => (r.gstr2b_doc_id ? setSel(r) : undefined)} /> : <Loading />}
      {sel && <div className="confirm">{String(sel.supplier_gstin)} · {String(sel.doc_no)}: 
        <label className={`inline-label${err && /remark|why/i.test(err) ? " bad" : ""}`}>Remark<input value={remark} placeholder="Needed to reject" onChange={(e) => setRemark(e.target.value)} /></label>
        <button onClick={() => act("accept")}>Accept</button><button onClick={() => act("reject")}>Reject</button>
        <button onClick={() => act("pending")}>Pending</button><button className="link" onClick={() => setSel(null)}>Close</button></div>}
    </div>
  );
}
